import { useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../lib/api'
import { useAuthStore } from '../../stores/auth'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import {
  NotaRowCells,
  NotaHeaderEA,
  TEMPLATE_OPTIONS,
  type NotaContabila,
} from '../../components/deconturi/NoteContabileShared'
import { formatDocns } from '../../lib/decontFormat'
import { grupeazaNeinchise } from '../../lib/deconturiNeinchise'
import { ETICHETE_PERIOADA, interval, lunaDinZiISO, lunaInCuvinte } from '../../lib/perioade'
import { ChevronRight } from 'lucide-react'
import { DateRangeField } from '../../components/ui/DateField'

interface NotaListItem extends NotaContabila {
  decont: {
    id: number
    numarDecont: number | null
    period: string
    status: 'new' | 'extracted' | 'pending_review' | 'verified' | 'exported'
    employee: { id: number; fullName: string }
  }
}

interface DecontListItem {
  id: number
  employeeId: number
  period: string
  status: 'new' | 'extracted' | 'pending_review' | 'verified' | 'exported'
  currency: 'LEI' | 'EUR'
  dueDate: string | null
  totalCheltuieli: string | null
  /** Facturile plătite de angajat (pe 401) — se scad din avans, ca bonurile. */
  totalFacturaDirecta: string | null
  numarDecont: number | null
  employee: { id: number; fullName: string }
  _count: { files: number; lines: number; noteContabile: number }
}

interface ScanResult {
  periodsScanned: number
  decontsCreated: number
  decontsExisting: number
  filesUpserted: number
  reopenedDeconts: number
  unmappedFolders: string[]
  errors: string[]
}

interface CostEntry {
  costUsd: number
  calls: number
  inputTokens: number
  outputTokens: number
}

interface CostsSummary {
  total: CostEntry
  byPeriod: Array<{ period: string } & CostEntry>
  byDecont: Array<{ decontId: number } & CostEntry>
}

/** Un rand din comparatia cu decontul semnat de angajat (fisierul din Nextcloud). */
interface RandCrossCheck {
  decontId: number
  period: string
  angajat: string
  status: DecontListItem['status']
  cross: 'match' | 'mismatch' | 'no_reference' | 'error'
  crmTotal: number
  manualTotal: number | null
  dif: number | null
  liniiCrm: number
  liniiManual: number | null
  potrivite: number | null
  mesaj?: string
}

interface RandProblema {
  lineId: number
  decontId: number
  period: string
  angajat: string
  vendorName: string | null
  docRef: string | null
  docDate: string | null
  amountTotal: number
  amountNet: number | null
  amountVat: number | null
  vatRate: number | null
  verdict: 'neconcordant' | 'ilizibil'
  motiv: string | null
}

interface RandPereche {
  decontId: number
  period: string
  angajat: string
  cota: number
  net: { lineId: number; vendorName: string | null; amountTotal: number }
  brut: { lineId: number; vendorName: string | null; amountTotal: number }
}

interface RaportSume {
  sumar: Record<string, number>
  sumaInJoc: number
  probleme: RandProblema[]
  perechi: RandPereche[]
}

interface CrossCheckToate {
  total: number
  potriviri: number
  neconcordante: number
  faraReferinta: number
  erori: number
  difNet: number
  difAbs: number
  randuri: RandCrossCheck[]
}

function fmtUsd(n: number): string {
  return '$' + n.toFixed(4)
}

function fmtLei(n: number | null): string {
  if (n == null) return '—'
  return n.toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const CROSS_LABEL: Record<RandCrossCheck['cross'], string> = {
  match: 'Se potrivește',
  mismatch: 'Diferă',
  no_reference: 'Fără decont semnat',
  error: 'Eroare',
}

const CROSS_CLASS: Record<RandCrossCheck['cross'], string> = {
  match: 'bg-green-100 text-green-700',
  mismatch: 'bg-red-100 text-red-700',
  no_reference: 'bg-b4l-bg text-b4l-text-muted',
  error: 'bg-amber-100 text-amber-700',
}

const STATUS_LABEL: Record<DecontListItem['status'], string> = {
  new: 'Nou',
  extracted: 'Citit automat',
  pending_review: 'În verificare',
  verified: 'Verificat',
  exported: 'Exportat',
}

const STATUS_CLASS: Record<DecontListItem['status'], string> = {
  new: 'bg-b4l-bg text-b4l-text-muted',
  extracted: 'bg-blue-100 text-blue-700',
  pending_review: 'bg-amber-100 text-amber-700',
  verified: 'bg-green-100 text-green-700',
  exported: 'bg-emerald-200 text-emerald-800',
}

function isOverdue(d: DecontListItem): boolean {
  return d.dueDate != null && new Date(d.dueDate) < new Date() && d.status !== 'exported'
}

interface RandAcoperire {
  employeeId: number
  nume: string
  decontId: number | null
  bonuri: number
  stare: 'lipsa' | 'neconfirmat' | 'confirmat' | 'la_contabilitate'
  confirmatLa: string | null
}

export function DeconturiHubPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const canWrite = useAuthStore((st) => st.permissions.includes('hr.deconturi:write'))
  const [searchParams, setSearchParams] = useSearchParams()

  // Tab + filtre persistate in URL — sa supravietuiasca navigarii (enter decont -> back).
  /*
    Trei tab-uri, si al treilea raspunde la alta intrebare decat celelalte doua.

    „Deconturi" si „Note contabile" arata CONTINUT. „Situatie" arata CUM STAM: cat a costat
    citirea, cine a depus si cine nu, si ce a ramas neinchis. Trei carduri care inainte stateau
    peste lista de deconturi si o impingeau in jos, desi nu se citesc la fel de des (Alexandru,
    19.09.2026).
  */
  const tabCerut = searchParams.get('tab')
  const viewTab: 'deconturi' | 'note' | 'situatie' =
    tabCerut === 'note' ? 'note' : tabCerut === 'situatie' ? 'situatie' : 'deconturi'
  /*
    Ziua pana la care se mai pot pune bonuri pe luna precedenta.

    Sta aici, nu intr-o pagina de setari proprie: e un singur camp, iar cine il schimba se uita
    oricum la deconturi cand o face.
  */
  const setari = useQuery<{ ziInchidereLuna: number | null }>({
    queryKey: ['deconturi', 'setari'],
    queryFn: async () => (await api.get('/deconturi/setari')).data,
  })
  /** Ziua de inchidere configurata. `null` = inchiderea e oprita. */
  const zi = setari.data?.ziInchidereLuna ?? null

  const salveazaZi = useMutation({
    mutationFn: async (zi: number | null) =>
      (await api.put('/deconturi/setari', { ziInchidereLuna: zi })).data,
    onSuccess: (d: { ziInchidereLuna: number | null }) => {
      void queryClient.invalidateQueries({ queryKey: ['deconturi', 'setari'] })
      toast.success(
        d.ziInchidereLuna == null
          ? 'Închiderea lunii e oprită — bonul intră în luna lui, oricât de târziu ar fi adus.'
          : `Luna precedentă se închide pe ${d.ziInchidereLuna}. Bonurile aduse după intră pe luna curentă.`,
      )
    },
    onError: () => toast.error('Setarea nu s-a salvat.'),
  })

  const [neinchiseDeschis, setNeinchiseDeschis] = useState(false)
  /** `employeeId-an` pentru anul desfăcut. Unul singur odată — altfel se umple ecranul la loc. */
  const [anDeschis, setAnDeschis] = useState<string | null>(null)

  /*
    Filtrul de perioada, ca interval de luni.

    Implicit: LUNA PRECEDENTA. Deconturile se lucreaza pentru luna incheiata, nu pentru cea in
    curs, iar pana acum pagina se deschidea pe „Toate" — adica pe tot istoricul, din care trebuia
    cautata de fiecare data luna care conta (Alexandru, 18.09.2026).

    Parametrul PREZENT DAR GOL inseamna „fara limita". Fara distinctia asta, golirea campului ar
    fi readus tacit valoarea implicita si n-ai fi putut vedea niciodata tot.
  */
  const implicit = useMemo(() => interval('luna_precedenta'), [])
  const fDeLa = searchParams.has('de_la') ? (searchParams.get('de_la') ?? '') : implicit.from
  const fPanaLa = searchParams.has('pana_la') ? (searchParams.get('pana_la') ?? '') : implicit.to

  /*
    Filtrul lucreaza pe ZILE, ca pe celelalte ecrane, dar deconturile sunt lunare. Ziua se taie
    la luna: `2025-01-01` → `2025.01`. „Anul precedent" devine astfel ianuarie–decembrie, fara
    nicio conversie speciala.
  */
  const caPerioada = (zi: string) => lunaDinZiISO(zi)
  const pDeLa = caPerioada(fDeLa)
  const pPanaLa = caPerioada(fPanaLa)

  /*
    Cine a depus si cine nu, pe luna aleasa.

    Aurora stia asta din cap, fiindca incarca ea totul. Cu zece oameni care isi incarca singuri
    bonurile, un decont care NU vine nu se vede nicaieri — tocmai fiindca nu exista.

    Se cere doar cand intervalul e o singura luna: „cine lipseste" n-are inteles pe un trimestru.
  */
  const lunaAcoperire = pDeLa && pDeLa === pPanaLa ? pDeLa : null
  const acoperire = useQuery<{ randuri: RandAcoperire[] }>({
    queryKey: ['deconturi', 'acoperire', lunaAcoperire],
    enabled: lunaAcoperire != null,
    queryFn: async () =>
      (await api.get('/deconturi/acoperire', { params: { period: lunaAcoperire } })).data,
  })

  const seteazaInterval = (de_la: string, pana_la: string) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.set('de_la', de_la)
        next.set('pana_la', pana_la)
        return next
      },
      { replace: true },
    )
  }

  /** Aceleasi patru perioade ca pe celelalte pagini, plus „Toate". */
  const scurtaturi = useMemo(() => {
    const p: { cheie: string; eticheta: string; de_la: string; pana_la: string }[] = (
      ['luna_curenta', 'luna_precedenta', 'an_curent', 'an_precedent'] as const
    ).map((n) => {
      const i = interval(n)
      return { cheie: n, eticheta: ETICHETE_PERIOADA[n], de_la: i.from, pana_la: i.to }
    })
    return [...p, { cheie: 'toate', eticheta: 'Toate', de_la: '', pana_la: '' }]
  }, [])
  const scurtaturaActiva = scurtaturi.find((x) => x.de_la === fDeLa && x.pana_la === fPanaLa)?.cheie

  const fEmployee = searchParams.get('employee') ?? ''
  const fStatus = searchParams.get('status') ?? ''
  const fTemplate = searchParams.get('template') ?? ''
  const fCont = searchParams.get('cont') ?? ''
  const fPartener = searchParams.get('partener') ?? ''

  const setParam = (key: string, value: string) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        if (value) next.set(key, value)
        else next.delete(key)
        return next
      },
      { replace: true },
    )
  }
  const setViewTab = (v: 'deconturi' | 'note' | 'situatie') =>
    setParam('tab', v === 'deconturi' ? '' : v)

  const setFEmployee = (v: string) => setParam('employee', v)
  const setFStatus = (v: string) => setParam('status', v)
  const setFTemplate = (v: string) => setParam('template', v)
  const setFCont = (v: string) => setParam('cont', v)
  const setFPartener = (v: string) => setParam('partener', v)

  const query = useQuery<{ deconturi: DecontListItem[] }>({
    queryKey: ['deconturi'],
    queryFn: async () => (await api.get<{ deconturi: DecontListItem[] }>('/deconturi')).data,
  })

  const noteQuery = useQuery<{ note: NotaListItem[] }>({
    queryKey: [
      'note-contabile-list',
      pDeLa,
      pPanaLa,
      fEmployee,
      fStatus,
      fTemplate,
      fCont,
      fPartener,
    ],
    enabled: viewTab === 'note',
    queryFn: async () => {
      const params: Record<string, string> = {}
      if (pDeLa) params.periodFrom = pDeLa
      if (pPanaLa) params.periodTo = pPanaLa
      if (fEmployee) params.employeeId = fEmployee
      if (fStatus) params.status = fStatus
      if (fTemplate) params.template = fTemplate
      if (fCont) params.cont = fCont
      if (fPartener) params.partener = fPartener
      return (await api.get<{ note: NotaListItem[] }>('/deconturi/note-contabile', { params })).data
    },
  })

  const costsQuery = useQuery<CostsSummary>({
    queryKey: ['deconturi-costs'],
    queryFn: async () => (await api.get<CostsSummary>('/deconturi/costs')).data,
  })

  const scanMutation = useMutation({
    mutationFn: async () =>
      (
        await api.post<{ result: ScanResult }>('/deconturi/scanner/trigger', undefined, {
          timeout: 120_000,
        })
      ).data,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['deconturi'] })
      queryClient.invalidateQueries({ queryKey: ['deconturi-costs'] })
      const r = data.result
      if (r.errors.length > 0) {
        toast.error(`Scanare cu erori: ${r.errors[0]}`)
      } else {
        toast.success(
          `Scanat: ${r.decontsCreated} deconturi noi, ${r.filesUpserted} fișiere` +
            (r.reopenedDeconts > 0 ? `, ${r.reopenedDeconts} redeschise` : ''),
        )
      }
      if (r.unmappedFolders.length > 0) {
        toast.warning(
          `${r.unmappedFolders.length} foldere din Nextcloud nu corespund niciunui angajat — verifică „Folder Nextcloud” în Setări → Angajați`,
        )
      }
    },
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? 'Eroare la scanare'
      toast.error(msg)
    },
  })

  // Comparatia cu deconturile semnate, pe toate deconturile filtrate. Descarca
  // un xlsx din Nextcloud pentru fiecare, deci dureaza — de aceea e la cerere,
  // nu la incarcarea paginii.
  /*
    Reia bonurile pe care OCR-ul n-a reusit sa le citeasca.

    Workerul ia doar fisierele `pending`, deci unul ajuns pe `failed` ramane acolo pentru
    totdeauna. Pe 20.09.2026 erau 22 de bonuri necitite, unele din iulie, iar cinci deconturi
    aratau 0,00 lei desi decontul semnat avea sume pe el.
  */
  const reiaNecitite = useMutation({
    mutationFn: async () =>
      (
        await api.post<{ reluate: number; motive: { motiv: string; cate: number }[] }>(
          '/deconturi/reia-bonurile-necitite',
          undefined,
          { timeout: 120_000 },
        )
      ).data,
    onSuccess: (d) => {
      void queryClient.invalidateQueries({ queryKey: ['deconturi'] })
      toast.success(
        d.reluate === 0
          ? 'Nu e niciun bon necitit.'
          : `${d.reluate} ${d.reluate === 1 ? 'bon reluat' : 'bonuri reluate'}. Citirea se face în fundal și durează câteva minute.`,
      )
    },
    onError: () => toast.error('Nu s-au putut relua bonurile.'),
  })

  /*
    Repune bonurile pe luna decontului semnat (aprilie-august 2026).

    Reincadrarea nu le aduce inapoi singura — ea doar nu le mai misca de acum incolo. Butonul
    ramane si dupa reparatie: orice mutare gresita in masa are nevoie de un drum inapoi.
  */
  const repuneLuna = useMutation({
    mutationFn: async () =>
      (
        await api.post<{
          mutate: { fileId: number; din: string; in: string }[]
          sarite: { fileId: number }[]
          esuate: { fileId: number; motiv: string }[]
        }>('/deconturi/repune-pe-luna-semnata', undefined, { timeout: 300_000 })
      ).data,
    onSuccess: (d) => {
      void queryClient.invalidateQueries({ queryKey: ['deconturi'] })
      const parti = [
        d.mutate.length === 0
          ? 'Nimic de repus — toate bonurile sunt pe luna lor.'
          : `${d.mutate.length} ${d.mutate.length === 1 ? 'bon repus' : 'bonuri repuse'} pe luna decontului semnat.`,
      ]
      if (d.sarite.length > 0) {
        parti.push(`${d.sarite.length} sărite (decont deja exportat).`)
      }
      if (d.esuate.length > 0) parti.push(`${d.esuate.length} nu s-au putut muta.`)
      toast.success(parti.join(' '))
    },
    onError: () => toast.error('Nu s-au putut repune bonurile.'),
  })

  const verificaToate = useMutation({
    mutationFn: async () => {
      const params: Record<string, string> = {}
      if (pDeLa) params.periodFrom = pDeLa
      if (pPanaLa) params.periodTo = pPanaLa
      if (fEmployee) params.employeeId = fEmployee
      if (fStatus) params.status = fStatus
      return (
        await api.get<{ rezultat: CrossCheckToate }>('/deconturi/cross-check', {
          params,
          timeout: 300_000,
        })
      ).data.rezultat
    },
    onSuccess: (r) => {
      if (r.neconcordante === 0) {
        toast.success(`${r.potriviri} deconturi se potrivesc cu hârtia, nicio diferență`)
      } else {
        toast.warning(
          `${r.neconcordante} deconturi diferă — ${fmtLei(r.difNet)} lei în plus în CRM`,
        )
      }
    },
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? 'Eroare la verificare'
      toast.error(msg)
    },
  })

  // Controlul aritmetic ruleaza in umbra la fiecare citire; asta doar aduna verdictele deja
  // scrise, deci e ieftin si se poate cere oricand.
  const verificaSume = useMutation({
    mutationFn: async () => {
      const params: Record<string, string> = {}
      if (pDeLa) params.periodFrom = pDeLa
      if (pPanaLa) params.periodTo = pPanaLa
      if (fEmployee) params.employeeId = fEmployee
      return (await api.get<{ raport: RaportSume }>('/deconturi/verificare-sume', { params })).data
        .raport
    },
    onSuccess: (r) => {
      const n = r.probleme.length + r.perechi.length
      if (n === 0) toast.success('Toate sumele se închid')
      else toast.warning(`${n} linii de verificat`)
    },
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? 'Eroare la verificare'
      toast.error(msg)
    },
  })

  const genereazaBulk = useMutation({
    mutationFn: async ({ force }: { force: boolean }) => {
      const body: Record<string, unknown> = {}
      if (pDeLa) body.periodFrom = pDeLa
      if (pPanaLa) body.periodTo = pPanaLa
      if (fEmployee) body.employeeId = Number(fEmployee)
      if (fStatus) body.status = fStatus
      if (force) body.force = true
      return (
        await api.post<{
          result: {
            processed: number
            generated: number
            regenerated: number
            skipped: number
            numbered: number
          }
        }>('/deconturi/note-contabile/genereaza-bulk', body, { timeout: 180_000 })
      ).data
    },
    onSuccess: (d) => {
      queryClient.invalidateQueries({ queryKey: ['deconturi'] })
      queryClient.invalidateQueries({ queryKey: ['note-contabile-list'] })
      const parts: string[] = []
      if (d.result.generated > 0) parts.push(`${d.result.generated} generate`)
      if (d.result.regenerated > 0) parts.push(`${d.result.regenerated} regenerate`)
      if (d.result.skipped > 0) parts.push(`${d.result.skipped} sărite`)
      if (d.result.numbered > 0) parts.push(`${d.result.numbered} numere asignate`)
      toast.success(`Note contabile: ${parts.join(', ') || 'nicio modificare'}`)
    },
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? 'Eroare la generare note'
      toast.error(msg)
    },
  })

  const exportXlsx = useMutation({
    mutationFn: async () => {
      const params: Record<string, string> = {}
      if (pDeLa) params.periodFrom = pDeLa
      if (pPanaLa) params.periodTo = pPanaLa
      if (fEmployee) params.employeeId = fEmployee
      if (fStatus) params.status = fStatus
      if (fTemplate) params.template = fTemplate
      if (fCont) params.cont = fCont
      if (fPartener) params.partener = fPartener
      const res = await api.get('/deconturi/note-contabile/export-xlsx', {
        params,
        responseType: 'blob',
      })
      const periodLabel = pDeLa && pDeLa === pPanaLa ? pDeLa : 'multi'
      const today = new Date().toISOString().slice(0, 10)
      const fileName = `note-contabile-${periodLabel}-${today}.xlsx`
      const url = URL.createObjectURL(res.data as Blob)
      const a = document.createElement('a')
      a.href = url
      a.download = fileName
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    },
    onSuccess: () => toast.success('Excel descărcat — verifică folderul Downloads'),
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? 'Eroare la export Excel'
      toast.error(msg)
    },
  })

  const deconturi = useMemo(() => query.data?.deconturi ?? [], [query.data])
  const overdue = useMemo(() => deconturi.filter(isOverdue), [deconturi])
  const costs = costsQuery.data
  const costByDecont = useMemo(
    () => new Map((costs?.byDecont ?? []).map((d) => [d.decontId, d])),
    [costs],
  )

  const angajati = useMemo(() => {
    const m = new Map<number, string>()
    for (const d of deconturi) m.set(d.employeeId, d.employee.fullName)
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]))
  }, [deconturi])
  const filtered = useMemo(
    () =>
      deconturi.filter(
        (d) =>
          (!pDeLa || d.period >= pDeLa) &&
          (!pPanaLa || d.period <= pPanaLa) &&
          (!fEmployee || d.employeeId === Number(fEmployee)) &&
          (!fStatus || d.status === fStatus),
      ),
    [deconturi, pDeLa, pPanaLa, fEmployee, fStatus],
  )

  const byPeriod = useMemo(() => {
    const map = new Map<string, DecontListItem[]>()
    for (const d of filtered) {
      const arr = map.get(d.period) ?? []
      arr.push(d)
      map.set(d.period, arr)
    }
    return [...map.entries()].sort((a, b) => b[0].localeCompare(a[0]))
  }, [filtered])

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Deconturi angajați</h1>
        <Button
          onClick={() => scanMutation.mutate()}
          loading={scanMutation.isPending}
          title="Verifică folderele din cloud pentru bonuri apărute între timp. Se face oricum automat, la 15 minute."
        >
          Caută fișiere noi
        </Button>
      </div>

      <div className="flex gap-1 border-b border-b4l-border">
        <button
          type="button"
          onClick={() => setViewTab('deconturi')}
          className={`px-3 py-1.5 text-sm font-medium ${
            viewTab === 'deconturi'
              ? 'border-b-2 border-b4l-text text-b4l-text'
              : 'text-b4l-text-muted hover:text-b4l-text'
          }`}
        >
          Deconturi
        </button>
        <button
          type="button"
          onClick={() => setViewTab('note')}
          className={`px-3 py-1.5 text-sm font-medium ${
            viewTab === 'note'
              ? 'border-b-2 border-b4l-text text-b4l-text'
              : 'text-b4l-text-muted hover:text-b4l-text'
          }`}
        >
          Note contabile
        </button>
        <button
          type="button"
          onClick={() => setViewTab('situatie')}
          className={`px-3 py-1.5 text-sm font-medium ${
            viewTab === 'situatie'
              ? 'border-b-2 border-b4l-text text-b4l-text'
              : 'text-b4l-text-muted hover:text-b4l-text'
          }`}
        >
          Situație
        </button>
      </div>

      <Card>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-wrap items-end gap-2">
            <div className="flex flex-wrap gap-1.5">
              {scurtaturi.map((sc) => (
                <button
                  key={sc.cheie}
                  type="button"
                  onClick={() => seteazaInterval(sc.de_la, sc.pana_la)}
                  className={`rounded-md border px-3 py-1.5 text-sm transition ${
                    scurtaturaActiva === sc.cheie
                      ? 'border-b4l-blue bg-b4l-blue font-medium text-white'
                      : 'border-b4l-border bg-white text-b4l-text hover:border-b4l-blue hover:text-b4l-blue-dark'
                  }`}
                >
                  {sc.eticheta}
                </button>
              ))}
            </div>
            <div className="w-full sm:w-72">
              <DateRangeField
                fromValue={fDeLa}
                toValue={fPanaLa}
                onChange={(f, t) => seteazaInterval(f, t)}
              />
            </div>
          </div>
          <label className="flex flex-col gap-1 text-xs text-b4l-text-muted">
            Angajat
            <select
              value={fEmployee}
              onChange={(e) => setFEmployee(e.target.value)}
              className="rounded border border-b4l-border bg-white px-2 py-1 text-sm text-b4l-text"
            >
              <option value="">Toți</option>
              {angajati.map(([id, name]) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs text-b4l-text-muted">
            Stare
            <select
              value={fStatus}
              onChange={(e) => setFStatus(e.target.value)}
              className="rounded border border-b4l-border bg-white px-2 py-1 text-sm text-b4l-text"
            >
              <option value="">Toate</option>
              {(Object.keys(STATUS_LABEL) as DecontListItem['status'][]).map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABEL[s]}
                </option>
              ))}
            </select>
          </label>
          {/*
            Ziua de inchidere a lunii precedente. Doar pentru cine poate scrie pe deconturi.

            Textul spune CONSECINTA, nu numele campului: „ce se intampla cu un bon adus pe 15"
            e intrebarea pe care si-o pune omul, nu „care e valoarea lui ziInchidereLuna".
          */}
          {canWrite && (
            <label className="flex flex-col gap-1 text-xs text-b4l-text-muted">
              Luna precedentă se închide pe
              <select
                value={zi ?? ''}
                onChange={(e) =>
                  salveazaZi.mutate(e.target.value === '' ? null : Number(e.target.value))
                }
                disabled={setari.isLoading || salveazaZi.isPending}
                className="rounded border border-b4l-border bg-white px-2 py-1 text-sm text-b4l-text"
                title="Bonurile pentru luna precedentă aduse după ziua asta intră pe luna curentă"
              >
                <option value="">nu se închide</option>
                {/*
                  Lista e scurta intentionat, dar API-ul accepta orice zi intre 1 si 28. O valoare
                  din afara listei nu s-ar potrivi cu nicio optiune, iar browserul ar afisa prima —
                  „nu se închide". Adica selectorul ar spune ca inchiderea e OPRITA cand de fapt e
                  pornita. Deci valoarea curenta se adauga in lista daca nu e deja acolo.
                */}
                {[...new Set([5, 10, 15, 20, 25, ...(zi == null ? [] : [zi])])]
                  .sort((a, b) => a - b)
                  .map((z) => (
                    <option key={z} value={z}>
                      ziua {z}
                    </option>
                  ))}
              </select>
            </label>
          )}

          {(fDeLa !== implicit.from || fPanaLa !== implicit.to || fEmployee || fStatus) && (
            <Button
              variant="secondary"
              className="!px-2 !py-1 !text-xs"
              onClick={() => {
                // Inapoi la implicit — luna precedenta —, nu la „fara filtre".
                seteazaInterval(implicit.from, implicit.to)
                setFEmployee('')
                setFStatus('')
              }}
            >
              Resetează
            </Button>
          )}
          <div className="flex w-full flex-wrap items-center gap-2 md:ml-auto md:w-auto">
            <span className="text-xs text-b4l-text-muted">
              {filtered.length} deconturi filtrate
            </span>
            <Button
              variant="secondary"
              loading={repuneLuna.isPending}
              title="Aprilie–august: luna cheltuielii e cea de pe decontul semnat. Readuce bonurile plecate pe altă lună."
              onClick={() => repuneLuna.mutate()}
            >
              Repune pe luna semnată
            </Button>
            <Button
              variant="secondary"
              loading={reiaNecitite.isPending}
              title="Bonurile pe care citirea automată nu le-a putut citi rămân necitite. Butonul le pune din nou la rând."
              onClick={() => reiaNecitite.mutate()}
            >
              Reia bonurile necitite
            </Button>
            <Button
              variant="secondary"
              loading={verificaSume.isPending}
              title="Liniile la care baza, cota și totalul nu se potrivesc"
              onClick={() => verificaSume.mutate()}
            >
              Verifică sumele
            </Button>
            <Button
              variant="secondary"
              loading={verificaToate.isPending}
              disabled={filtered.length === 0}
              title="Compară fiecare decont din CRM cu decontul semnat din folderul lui de Nextcloud"
              onClick={() => verificaToate.mutate()}
            >
              Verifică față de hârtie
            </Button>
            <Button
              variant="secondary"
              loading={genereazaBulk.isPending}
              disabled={filtered.length === 0}
              onClick={() => {
                if (
                  window.confirm(
                    `Generezi note contabile pentru ${filtered.length} deconturi filtrate?\n\n` +
                      'Deconturile care au deja note contabile sunt sărite (nu se suprascriu corecțiile manuale).',
                  )
                ) {
                  genereazaBulk.mutate({ force: false })
                }
              }}
            >
              Generează note
            </Button>
            <Button
              loading={genereazaBulk.isPending}
              disabled={filtered.length === 0}
              onClick={() => {
                if (
                  window.confirm(
                    `Regenerez (FORȚAT) notele pentru ${filtered.length} deconturi filtrate?\n\n` +
                      'TOATE notele existente (inclusiv editările manuale) se ȘTERG și se recreează din bonuri cu noile reguli (categorie/cont/centru auto, TVA deductibil).\n\n' +
                      'Folosește asta când ai aplicat schimbări de logică și vrei să le aplici retroactiv.',
                  )
                ) {
                  genereazaBulk.mutate({ force: true })
                }
              }}
            >
              Regenerează (forțat)
            </Button>
          </div>
        </div>
      </Card>

      {verificaSume.data && (
        <Card>
          <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="text-sm font-semibold">Sume care nu se închid</h2>
            <button
              type="button"
              className="text-xs text-b4l-text-muted underline"
              onClick={() => verificaSume.reset()}
            >
              Închide
            </button>
          </div>
          <div className="mb-4 flex flex-wrap gap-x-6 gap-y-1 text-xs text-b4l-text-muted">
            <span>
              <b className="text-green-700">{verificaSume.data.sumar.ok ?? 0}</b> se închid
            </span>
            <span>
              <b className="text-blue-700">{verificaSume.data.sumar.completat ?? 0}</b> completate
              automat
            </span>
            <span>
              <b className="text-red-700">{verificaSume.data.sumar.neconcordant ?? 0}</b> nu se
              închid
            </span>
            <span>
              <b className="text-amber-700">{verificaSume.data.sumar.ilizibil ?? 0}</b> ilizibile
            </span>
            <span>
              <b>{verificaSume.data.sumar.fara_verdict ?? 0}</b> necitite încă
            </span>
            <span>
              Sumă în discuție: <b>{fmtLei(verificaSume.data.sumaInJoc)}</b> lei
            </span>
          </div>

          {verificaSume.data.perechi.length > 0 && (
            <div className="mb-4 rounded border border-amber-300 bg-amber-50 p-3">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-amber-800">
                Același document, citit o dată întreg și o dată fără TVA
              </p>
              {verificaSume.data.perechi.map((pr) => (
                <p key={`${pr.net.lineId}-${pr.brut.lineId}`} className="text-xs text-amber-900">
                  {pr.period} - {pr.angajat} - {pr.net.vendorName ?? '?'}{' '}
                  <b>{fmtLei(pr.net.amountTotal)}</b> urcat cu {pr.cota}% dă exact{' '}
                  <b>{fmtLei(pr.brut.amountTotal)}</b>, cât e pe {pr.brut.vendorName ?? '?'}.{' '}
                  <button
                    type="button"
                    className="underline"
                    onClick={() => navigate(`/hr/deconturi/${pr.decontId}`)}
                  >
                    Deschide decontul
                  </button>
                </p>
              ))}
            </div>
          )}

          {verificaSume.data.probleme.length === 0 ? (
            <p className="text-xs text-b4l-text-muted">Nicio linie cu sume care nu se închid.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-b4l-text-muted">
                  <tr className="border-b border-b4l-border">
                    <th className="py-2 pr-3 text-left font-medium">Perioada</th>
                    <th className="py-2 pr-3 text-left font-medium">Angajat</th>
                    <th className="py-2 pr-3 text-left font-medium">Furnizor</th>
                    <th className="py-2 pr-3 text-right font-medium">Total</th>
                    <th className="py-2 pr-3 text-right font-medium">Baza</th>
                    <th className="py-2 pr-3 text-right font-medium">TVA</th>
                    <th className="py-2 pr-3 text-right font-medium">Cota</th>
                    <th className="py-2 pr-3 text-left font-medium">Ce nu se închide</th>
                  </tr>
                </thead>
                <tbody>
                  {verificaSume.data.probleme.map((pb) => (
                    <tr
                      key={pb.lineId}
                      className="cursor-pointer border-b border-b4l-border/50 hover:bg-b4l-bg"
                      onClick={() => navigate(`/hr/deconturi/${pb.decontId}`)}
                    >
                      <td className="py-2 pr-3 tabular-nums">{pb.period}</td>
                      <td className="py-2 pr-3">{pb.angajat}</td>
                      <td className="py-2 pr-3">{pb.vendorName ?? '-'}</td>
                      <td className="py-2 pr-3 text-right font-medium tabular-nums">
                        {fmtLei(pb.amountTotal)}
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums">{fmtLei(pb.amountNet)}</td>
                      <td className="py-2 pr-3 text-right tabular-nums">{fmtLei(pb.amountVat)}</td>
                      <td className="py-2 pr-3 text-right tabular-nums">
                        {pb.vatRate == null ? '-' : `${pb.vatRate}%`}
                      </td>
                      <td className="py-2 pr-3 text-b4l-text-muted">{pb.motiv ?? '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="mt-3 text-xs text-b4l-text-muted">
                Verificarea rulează la citire și nu blochează nimic — deocamdată doar semnalează.
                Liniile completate automat sunt cele la care baza și TVA-ul s-au dedus din total și
                cota; nu cer nicio intervenție.
              </p>
            </div>
          )}
        </Card>
      )}

      {verificaToate.data && (
        <Card>
          <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="text-sm font-semibold">CRM față de deconturile semnate</h2>
            <button
              type="button"
              className="text-xs text-b4l-text-muted underline"
              onClick={() => verificaToate.reset()}
            >
              Închide
            </button>
          </div>
          <div className="mb-4 flex flex-wrap gap-x-6 gap-y-1 text-xs text-b4l-text-muted">
            <span>
              <b className="text-green-700">{verificaToate.data.potriviri}</b> se potrivesc
            </span>
            <span>
              <b className="text-red-700">{verificaToate.data.neconcordante}</b> diferă
            </span>
            <span>
              <b>{verificaToate.data.faraReferinta}</b> fără decont semnat
            </span>
            {verificaToate.data.erori > 0 && (
              <span>
                <b className="text-amber-700">{verificaToate.data.erori}</b> erori
              </span>
            )}
            <span>
              Diferență netă: <b>{fmtLei(verificaToate.data.difNet)}</b> lei
            </span>
            <span>
              De umblat: <b>{fmtLei(verificaToate.data.difAbs)}</b> lei
            </span>
          </div>
          {verificaToate.data.neconcordante === 0 ? (
            <p className="text-xs text-b4l-text-muted">
              Nicio diferență de sumă față de deconturile semnate.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-b4l-text-muted">
                  <tr className="border-b border-b4l-border">
                    <th className="py-2 pr-3 text-left font-medium">Perioadă</th>
                    <th className="py-2 pr-3 text-left font-medium">Angajat</th>
                    <th className="py-2 pr-3 text-right font-medium">CRM</th>
                    <th className="py-2 pr-3 text-right font-medium">Hârtie</th>
                    <th className="py-2 pr-3 text-right font-medium">Diferență</th>
                    <th className="py-2 pr-3 text-right font-medium">Linii</th>
                    <th className="py-2 pr-3 text-left font-medium">Stare</th>
                  </tr>
                </thead>
                <tbody>
                  {verificaToate.data.randuri
                    .filter((r) => r.cross === 'mismatch' || r.cross === 'error')
                    .map((r) => (
                      <tr
                        key={r.decontId}
                        className="cursor-pointer border-b border-b4l-border/50 hover:bg-b4l-bg"
                        onClick={() => navigate(`/hr/deconturi/${r.decontId}`)}
                      >
                        <td className="py-2 pr-3 tabular-nums">{r.period}</td>
                        <td className="py-2 pr-3">{r.angajat}</td>
                        <td className="py-2 pr-3 text-right tabular-nums">{fmtLei(r.crmTotal)}</td>
                        <td className="py-2 pr-3 text-right tabular-nums">
                          {fmtLei(r.manualTotal)}
                        </td>
                        <td
                          className={
                            'py-2 pr-3 text-right tabular-nums font-medium ' +
                            (r.dif != null && r.dif > 0 ? 'text-red-700' : 'text-amber-700')
                          }
                        >
                          {r.dif != null && r.dif > 0 ? '+' : ''}
                          {fmtLei(r.dif)}
                        </td>
                        <td className="py-2 pr-3 text-right tabular-nums text-b4l-text-muted">
                          {r.potrivite ?? 0}/{r.liniiCrm}
                          {r.liniiManual != null && r.liniiManual !== r.liniiCrm
                            ? ` (${r.liniiManual} pe hârtie)`
                            : ''}
                        </td>
                        <td className="py-2 pr-3">
                          <span className={'rounded px-2 py-0.5 ' + CROSS_CLASS[r.cross]}>
                            {r.mesaj ?? CROSS_LABEL[r.cross]}
                          </span>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
              <p className="mt-3 text-xs text-b4l-text-muted">
                Diferență pozitivă = CRM-ul are mai mult decât decontul semnat. „Linii” arată câte
                s-au potrivit din câte are CRM-ul. Apasă un rând ca să vezi comparația linie cu
                linie.
              </p>
            </div>
          )}
        </Card>
      )}

      {viewTab === 'note' && (
        <>
          <Card>
            <div className="flex flex-wrap items-end gap-3">
              <label className="flex flex-col gap-1 text-xs text-b4l-text-muted">
                Șablon notă
                <select
                  value={fTemplate}
                  onChange={(e) => setFTemplate(e.target.value)}
                  className="rounded border border-b4l-border bg-white px-2 py-1 text-sm text-b4l-text"
                >
                  <option value="">Toate</option>
                  {TEMPLATE_OPTIONS.filter((t) => t !== '').map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1 text-xs text-b4l-text-muted">
                Cont (debit sau credit)
                <input
                  value={fCont}
                  onChange={(e) => setFCont(e.target.value)}
                  placeholder="ex. 6022 sau 4426"
                  className="rounded border border-b4l-border bg-white px-2 py-1 text-sm text-b4l-text"
                />
              </label>
              <label className="flex flex-col gap-1 text-xs text-b4l-text-muted">
                Partener
                <input
                  value={fPartener}
                  onChange={(e) => setFPartener(e.target.value)}
                  placeholder="caută în nume partener"
                  className="rounded border border-b4l-border bg-white px-2 py-1 text-sm text-b4l-text"
                />
              </label>
              {(fTemplate || fCont || fPartener) && (
                <Button
                  variant="secondary"
                  className="!px-2 !py-1 !text-xs"
                  onClick={() => {
                    setFTemplate('')
                    setFCont('')
                    setFPartener('')
                  }}
                >
                  Resetează filtre note
                </Button>
              )}
              <div className="ml-auto flex items-center gap-3">
                <span className="text-xs text-b4l-text-muted">
                  {noteQuery.data?.note.length ?? 0} note găsite
                  {(noteQuery.data?.note.length ?? 0) >= 10000 && (
                    <span className="ml-1 text-amber-600">(limită 10000 — restrânge filtrele)</span>
                  )}
                </span>
                <Button
                  variant="secondary"
                  disabled={(noteQuery.data?.note.length ?? 0) === 0 || exportXlsx.isPending}
                  loading={exportXlsx.isPending}
                  onClick={() => exportXlsx.mutate()}
                  className="!px-2 !py-1 !text-xs"
                >
                  Exportă în Excel (EA)
                </Button>
              </div>
            </div>
          </Card>

          <Card>
            {noteQuery.isLoading ? (
              <p className="text-sm text-b4l-text-muted">Se încarcă...</p>
            ) : (noteQuery.data?.note.length ?? 0) === 0 ? (
              <p className="text-sm text-b4l-text-muted">
                Nicio notă contabilă găsită cu filtrele aplicate. Notele se generează din editorul
                unui decont sau din butonul „Generează note contabile” de mai sus.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-b4l-border text-left text-[10px] uppercase tracking-wide text-b4l-text-muted">
                      <th className="py-1 pr-2 font-medium">Perioadă</th>
                      <th className="py-1 pr-2 font-medium">Angajat</th>
                      <th className="py-1 pr-2 font-medium">Nr. decont</th>
                      <NotaHeaderEA />
                    </tr>
                  </thead>
                  <tbody>
                    {(noteQuery.data?.note ?? []).map((n) => (
                      <tr key={n.id} className="border-b border-b4l-border last:border-0">
                        <td className="py-1 pr-2 text-b4l-text-muted">{n.decont.period}</td>
                        <td className="py-1 pr-2">{n.decont.employee.fullName}</td>
                        <td className="py-1 pr-2">
                          <button
                            onClick={() => navigate(`/hr/deconturi/${n.decont.id}`)}
                            className="text-blue-600 hover:underline"
                          >
                            {formatDocns(
                              n.decont.employee.fullName,
                              n.decont.period,
                              n.decont.numarDecont,
                            ) ?? `#${n.decont.id}`}
                          </button>
                        </td>
                        <NotaRowCells
                          nota={n}
                          invalidateKeys={[
                            ['note-contabile-list'],
                            ['note-contabile', n.decont.id],
                          ]}
                        />
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="mt-2 text-[10px] text-b4l-text-muted">
                  Editează direct în celule (Tab/Enter pentru salvare). Modificările se reflectă și
                  în decontul respectiv.
                </p>
              </div>
            )}
          </Card>
        </>
      )}

      {viewTab === 'situatie' && costs && costs.total.calls > 0 && (
        <Card>
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <div>
              <span className="text-sm font-medium">Cost citire automată (OCR, Claude Vision)</span>
              <span className="ml-2 text-lg font-semibold tabular-nums">
                {fmtUsd(costs.total.costUsd)}
              </span>
              <span className="ml-2 text-xs text-b4l-text-muted">
                {costs.total.calls} apeluri ·{' '}
                {(costs.total.inputTokens + costs.total.outputTokens).toLocaleString('ro-RO')}{' '}
                tokens
              </span>
            </div>
          </div>
          {costs.byPeriod.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {costs.byPeriod.map((p) => (
                <span
                  key={p.period}
                  className="rounded bg-b4l-bg px-2 py-0.5 text-xs text-b4l-text-muted"
                >
                  {p.period}: <span className="tabular-nums">{fmtUsd(p.costUsd)}</span> ({p.calls})
                </span>
              ))}
            </div>
          )}
        </Card>
      )}

      {/*
        Deconturile neinchise cu termen depasit.

        Stau STRANSE. In mod normal aici nu e mai nimic — un decont neinchis cu termenul depasit
        e o exceptie, nu regula —, iar cand se aduna, lista era un zid de butoane cu acelasi nume
        repetat de cinci ori. Drumul e in trei pasi: deschizi sectiunea, vezi cine si in ce ani,
        apesi pe un an si vezi lunile (Alexandru, 18.09.2026).
      */}
      {/*
        Cine a depus si cine nu. Cei care LIPSESC primii: un decont care nu vine nu se vede
        nicaieri altundeva, tocmai fiindca nu exista.
      */}
      {viewTab === 'situatie' && lunaAcoperire && (acoperire.data?.randuri.length ?? 0) > 0 && (
        <Card>
          {(() => {
            const r = acoperire.data!.randuri
            const gata = r.filter((x) => x.stare !== 'lipsa' && x.stare !== 'neconfirmat')
            const lipsa = r.filter((x) => x.stare === 'lipsa')
            const neconfirmate = r.filter((x) => x.stare === 'neconfirmat')
            return (
              <>
                <div className="text-sm font-medium">
                  {gata.length} din {r.length} angajați au decontul gata pe{' '}
                  {lunaInCuvinte(lunaAcoperire)}
                </div>
                {lipsa.length === 0 && neconfirmate.length === 0 ? (
                  <p className="mt-0.5 text-xs text-b4l-green-dark">Nu lipsește nimeni.</p>
                ) : (
                  <div className="mt-2 space-y-1.5">
                    {lipsa.length > 0 && (
                      <div className="flex flex-wrap items-baseline gap-1.5">
                        <span className="text-xs font-medium text-b4l-red">
                          N-au încărcat nimic:
                        </span>
                        {lipsa.map((x) => (
                          <span
                            key={x.employeeId}
                            className="rounded bg-red-50 px-1.5 py-0.5 text-xs text-red-700"
                          >
                            {x.nume}
                          </span>
                        ))}
                      </div>
                    )}
                    {neconfirmate.length > 0 && (
                      <div className="flex flex-wrap items-baseline gap-1.5">
                        <span className="text-xs font-medium text-amber-700">
                          Au încărcat, dar n-au confirmat:
                        </span>
                        {neconfirmate.map((x) => (
                          <button
                            key={x.employeeId}
                            type="button"
                            onClick={() => x.decontId && navigate(`/hr/deconturi/${x.decontId}`)}
                            className="rounded bg-amber-50 px-1.5 py-0.5 text-xs text-amber-700 hover:bg-amber-100"
                          >
                            {x.nume}
                            <span className="ml-1 opacity-70">({x.bonuri})</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )
          })()}
        </Card>
      )}

      {viewTab === 'situatie' && overdue.length > 0 && (
        <Card>
          <button
            type="button"
            onClick={() => {
              setNeinchiseDeschis((v) => !v)
              setAnDeschis(null)
            }}
            className="flex w-full items-center gap-2 text-left text-sm font-medium text-amber-700"
          >
            <ChevronRight
              size={14}
              className={`transition-transform ${neinchiseDeschis ? 'rotate-90' : ''}`}
            />
            {overdue.length} deconturi neînchise cu termen depășit
          </button>

          {neinchiseDeschis && (
            <div className="mt-3 space-y-1">
              {grupeazaNeinchise(overdue).map((a) => (
                <div key={a.employeeId} className="rounded border border-b4l-border p-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium">{a.nume}</span>
                    <span className="text-xs text-b4l-text-muted">
                      {a.total} {a.total === 1 ? 'decont' : 'deconturi'}
                    </span>
                    <span className="flex flex-wrap gap-1">
                      {a.ani.map((an) => {
                        const cheie = `${a.employeeId}-${an.an}`
                        const deschis = anDeschis === cheie
                        return (
                          <button
                            key={an.an}
                            type="button"
                            onClick={() => setAnDeschis(deschis ? null : cheie)}
                            className={`rounded border px-2 py-0.5 text-xs ${
                              deschis
                                ? 'border-amber-400 bg-amber-100 font-medium text-amber-800'
                                : 'border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100'
                            }`}
                          >
                            {an.an}
                            <span className="ml-1 text-[10px] opacity-70">
                              ({an.deconturi.length})
                            </span>
                          </button>
                        )
                      })}
                    </span>
                  </div>

                  {a.ani
                    .filter((an) => anDeschis === `${a.employeeId}-${an.an}`)
                    .map((an) => (
                      <div key={an.an} className="mt-2 flex flex-wrap gap-1 border-t pt-2">
                        {an.deconturi.map((d) => (
                          <button
                            key={d.id}
                            type="button"
                            onClick={() => navigate(`/hr/deconturi/${d.id}`)}
                            className="rounded border border-b4l-border px-2 py-1 text-xs hover:bg-b4l-bg-soft"
                          >
                            {lunaInCuvinte(d.period)}
                          </button>
                        ))}
                      </div>
                    ))}
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/*
        Tabul gol arata la fel ca unul stricat. Cand nu e nimic de spus, se SPUNE ca nu e nimic —
        si se spune ce inseamna asta: nimeni nu lipseste, nimic nu e restant.
      */}
      {viewTab === 'situatie' &&
        !(costs && costs.total.calls > 0) &&
        !(lunaAcoperire && (acoperire.data?.randuri.length ?? 0) > 0) &&
        overdue.length === 0 && (
          <Card>
            <p className="text-sm text-b4l-text-muted">
              Nimic de semnalat pe perioada aleasă: niciun decont restant și niciun angajat care
              lipsește.
            </p>
          </Card>
        )}

      {viewTab === 'deconturi' && query.isLoading ? (
        <Card>
          <p className="text-sm text-b4l-text-muted">Se încarcă...</p>
        </Card>
      ) : viewTab === 'deconturi' && byPeriod.length === 0 ? (
        <Card>
          <div className="rounded border border-dashed border-b4l-border p-8 text-center text-sm text-b4l-text-muted">
            Niciun decont. Apasă „Caută fișiere noi” ca să aduci folderele din Nextcloud (
            <code>/DECONTURI/</code>).
          </div>
        </Card>
      ) : viewTab === 'deconturi' ? (
        byPeriod.map(([period, items]) => {
          const totalsByCurrency = new Map<string, number>()
          let totalNote = 0
          let totalRanduri = 0
          let totalFisiere = 0
          for (const d of items) {
            const t = totalDecont(d)
            if (t != null) {
              const prev = totalsByCurrency.get(d.currency) ?? 0
              totalsByCurrency.set(d.currency, prev + t)
            }
            totalNote += d._count.noteContabile
            totalRanduri += d._count.lines
            totalFisiere += d._count.files
          }
          const fmtAmt = (n: number, cur: string) =>
            `${n.toLocaleString('ro-RO', { minimumFractionDigits: 2 })} ${cur}`
          const totalsLabel =
            [...totalsByCurrency.entries()].map(([cur, n]) => fmtAmt(n, cur)).join(' + ') || '—'
          const sortedItems = [...items].sort((a, b) =>
            a.employee.fullName.localeCompare(b.employee.fullName),
          )
          return (
            <Card key={period}>
              <h2 className="mb-3 text-sm font-semibold">{period}</h2>
              <div className="hidden overflow-x-auto md:block">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-b4l-border text-left text-xs uppercase tracking-wide text-b4l-text-muted">
                      <th className="py-2 pr-3 font-medium">Angajat</th>
                      <th className="py-2 pr-3 font-medium">Fișiere</th>
                      <th className="py-2 pr-3 font-medium">Rânduri</th>
                      <th className="py-2 pr-3 font-medium">Note</th>
                      <th className="py-2 pr-3 font-medium">Total</th>
                      <th className="py-2 pr-3 font-medium">Cost citire automată</th>
                      <th className="py-2 pr-3 font-medium">Stare</th>
                      <th className="py-2 pr-3 font-medium">Termen</th>
                      <th className="py-2 pr-3 font-medium"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedItems.map((d) => (
                      <tr key={d.id} className="border-b border-b4l-border last:border-0">
                        <td className="py-2 pr-3 font-medium">{d.employee.fullName}</td>
                        <td className="py-2 pr-3 text-center">{d._count.files}</td>
                        <td className="py-2 pr-3 text-center">{d._count.lines}</td>
                        <td className="py-2 pr-3 text-center">
                          {d._count.noteContabile > 0 ? (
                            <span className="text-green-600">✓ {d._count.noteContabile}</span>
                          ) : (
                            <span className="text-b4l-text-muted">—</span>
                          )}
                        </td>
                        <td className="py-2 pr-3 tabular-nums">
                          <TotalDecont d={d} />
                        </td>
                        <td className="py-2 pr-3 tabular-nums text-xs text-b4l-text-muted">
                          {costByDecont.has(d.id) ? fmtUsd(costByDecont.get(d.id)!.costUsd) : '—'}
                        </td>
                        <td className="py-2 pr-3">
                          <span
                            className={`rounded px-2 py-0.5 text-[10px] ${STATUS_CLASS[d.status]}`}
                          >
                            {STATUS_LABEL[d.status]}
                            {(() => {
                              const x = formatDocns(d.employee.fullName, period, d.numarDecont)
                              return x ? ` · ${x}` : ''
                            })()}
                          </span>
                        </td>
                        <td className="py-2 pr-3">
                          {isOverdue(d) ? (
                            <span className="rounded bg-red-100 px-2 py-0.5 text-[10px] text-red-700">
                              depășit
                            </span>
                          ) : d.dueDate ? (
                            <span className="text-xs text-b4l-text-muted">
                              {new Date(d.dueDate).toLocaleDateString('ro-RO')}
                            </span>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="py-2 pr-3">
                          <Button
                            variant="secondary"
                            onClick={() => navigate(`/hr/deconturi/${d.id}`)}
                            className="!px-2 !py-1 !text-xs"
                          >
                            Deschide
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-b4l-border text-sm font-semibold">
                      <td className="py-2 pr-3">Total {items.length} deconturi</td>
                      <td className="py-2 pr-3 text-center">{totalFisiere}</td>
                      <td className="py-2 pr-3 text-center">{totalRanduri}</td>
                      <td className="py-2 pr-3 text-center">{totalNote}</td>
                      <td className="py-2 pr-3 tabular-nums">{totalsLabel}</td>
                      <td colSpan={4} />
                    </tr>
                  </tfoot>
                </table>
              </div>
              <ul className="space-y-2 md:hidden">
                {sortedItems.map((d) => {
                  const docns = formatDocns(d.employee.fullName, period, d.numarDecont)
                  return (
                    <li
                      key={d.id}
                      className="rounded border border-b4l-border bg-b4l-bg-soft p-3 text-xs"
                    >
                      <div className="mb-2 flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-semibold">
                            {d.employee.fullName}
                          </div>
                          <div className="mt-1 flex flex-wrap items-center gap-1">
                            <span
                              className={`rounded px-2 py-0.5 text-[10px] ${STATUS_CLASS[d.status]}`}
                            >
                              {STATUS_LABEL[d.status]}
                              {docns ? ` · ${docns}` : ''}
                            </span>
                            {isOverdue(d) ? (
                              <span className="rounded bg-red-100 px-2 py-0.5 text-[10px] text-red-700">
                                depășit
                              </span>
                            ) : d.dueDate ? (
                              <span className="text-[10px] text-b4l-text-muted">
                                termen {new Date(d.dueDate).toLocaleDateString('ro-RO')}
                              </span>
                            ) : null}
                          </div>
                        </div>
                        <Button
                          variant="secondary"
                          onClick={() => navigate(`/hr/deconturi/${d.id}`)}
                          className="!px-2 !py-1 !text-xs"
                        >
                          Deschide
                        </Button>
                      </div>
                      <div className="grid grid-cols-4 gap-1 border-t border-b4l-border pt-2 text-center">
                        <div>
                          <div className="text-[10px] text-b4l-text-muted">Fișiere</div>
                          <div className="font-semibold">{d._count.files}</div>
                        </div>
                        <div>
                          <div className="text-[10px] text-b4l-text-muted">Rânduri</div>
                          <div className="font-semibold">{d._count.lines}</div>
                        </div>
                        <div>
                          <div className="text-[10px] text-b4l-text-muted">Note</div>
                          <div className="font-semibold">
                            {d._count.noteContabile > 0 ? (
                              <span className="text-green-600">✓ {d._count.noteContabile}</span>
                            ) : (
                              <span className="text-b4l-text-muted">—</span>
                            )}
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] text-b4l-text-muted">Total</div>
                          <div className="font-semibold tabular-nums">
                            <TotalDecont d={d} />
                          </div>
                        </div>
                      </div>
                    </li>
                  )
                })}
                <li className="rounded border border-b4l-border bg-white p-3 text-xs font-semibold">
                  <div className="mb-1">Total {items.length} deconturi</div>
                  <div className="grid grid-cols-4 gap-1 text-center">
                    <div>
                      <div className="text-[10px] font-normal text-b4l-text-muted">Fișiere</div>
                      <div>{totalFisiere}</div>
                    </div>
                    <div>
                      <div className="text-[10px] font-normal text-b4l-text-muted">Rânduri</div>
                      <div>{totalRanduri}</div>
                    </div>
                    <div>
                      <div className="text-[10px] font-normal text-b4l-text-muted">Note</div>
                      <div>{totalNote}</div>
                    </div>
                    <div>
                      <div className="text-[10px] font-normal text-b4l-text-muted">Total</div>
                      <div className="tabular-nums">{totalsLabel}</div>
                    </div>
                  </div>
                </li>
              </ul>
            </Card>
          )
        })
      ) : null}
    </div>
  )
}

/** Valoarea decontului: bonuri (+ diurnă, km) și facturi directe. `null` cât timp n-are rânduri. */
function totalDecont(d: DecontListItem): number | null {
  if (d.totalCheltuieli == null && d.totalFacturaDirecta == null) return null
  return Number(d.totalCheltuieli ?? 0) + Number(d.totalFacturaDirecta ?? 0)
}

function TotalDecont({ d }: { d: DecontListItem }) {
  const t = totalDecont(d)
  if (t == null) return <>—</>
  const fmtAmt = (n: number) =>
    `${n.toLocaleString('ro-RO', { minimumFractionDigits: 2 })} ${d.currency}`
  const facturi = Number(d.totalFacturaDirecta ?? 0)
  return (
    <>
      {fmtAmt(t)}
      {facturi !== 0 && (
        <div className="text-[10px] font-normal text-b4l-text-muted">
          bonuri {fmtAmt(Number(d.totalCheltuieli ?? 0))} · facturi {fmtAmt(facturi)}
        </div>
      )}
    </>
  )
}
